"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useCallback, useEffect } from "react";
import dynamic from "next/dynamic";
import { useNeumorphicTheme } from "@/hooks/useNeumorphicTheme";
const DottedMap = dynamic(() => import("@/components/ui/dotted-map").then(mod => mod.DottedMap), {
  ssr: false,
  loading: () => <span className="flex w-full h-full items-center justify-center bg-zinc-950 border-2 border-white text-white">
        <span className="text-xs font-black uppercase tracking-wider animate-pulse">
          Loading Map...
        </span>
      </span>
});

// Cairo, Egypt coordinates
const EGYPT_MARKERS = [{
  lat: 30.0444,
  lng: 31.2357,
  size: 2,
  pulse: true
}];
export default function EgyptMapTooltip(t0) {
  const $ = _c(30);
  const {
    children
  } = t0;
  const [visible, setVisible] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);
  const hideTimeout = useRef(null);
  const isNeumorphic = useNeumorphicTheme();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        const handle = (window as unknown as {
          requestIdleCallback: (cb: () => void) => number;
        }).requestIdleCallback(() => {
          setHasBeenShown(true);
        });
        return () => {
          if ("cancelIdleCallback" in window) {
            (window as unknown as {
              cancelIdleCallback: (id: number) => void;
            }).cancelIdleCallback(handle);
          }
        };
      } else {
        const timer = setTimeout(() => {
          setHasBeenShown(true);
        }, 300);
        return () => clearTimeout(timer);
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (hideTimeout.current) {
        clearTimeout(hideTimeout.current);
        hideTimeout.current = null;
      }
      setHasBeenShown(true);
      setVisible(true);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const show = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      hideTimeout.current = setTimeout(() => setVisible(false), 120);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const hide = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e => {
      e.preventDefault();
      setVisible(_temp);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = `
          egypt-map-tooltip
          pointer-events-none
          absolute z-50
          top-[110%] left-1/2 -translate-x-1/2
          sm:top-auto sm:bottom-[110%]
          w-[240px] sm:w-[280px]
          transition-all duration-200 ease-out
          ${isNeumorphic ? "bg-[#e0e5ec] rounded-2xl shadow-[6px_6px_12px_rgba(163,177,198,0.6),_-6px_-6px_12px_rgba(255,255,255,0.5)] border border-transparent text-black" : "bg-black border-4 border-white shadow-[8px_8px_0px_rgba(255,255,255,0.3)] text-white"}
          ${visible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 -translate-y-2 scale-95 sm:translate-y-2"}
        `;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      transformOrigin: "top center"
    };
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      height: 150
    };
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== hasBeenShown || $[8] !== isNeumorphic) {
    t9 = hasBeenShown && <DottedMap markers={EGYPT_MARKERS} dotColor={isNeumorphic ? "#4b5563" : "#fff"} markerColor={isNeumorphic ? "#4b5563" : "#fff"} dotRadius={0.35} pulse={true} mapSamples={3000} />;
    $[7] = hasBeenShown;
    $[8] = isNeumorphic;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t9) {
    t10 = <span className="block p-2" style={t8}>{t9}</span>;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const t11 = isNeumorphic ? "10px solid transparent" : "10px solid white";
  let t12;
  if ($[12] !== t11) {
    t12 = <span className="absolute sm:hidden left-1/2 -translate-x-1/2 top-[-10px] w-0 h-0" style={{
      borderLeft: "10px solid transparent",
      borderRight: "10px solid transparent",
      borderBottom: t11
    }} />;
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const t13 = isNeumorphic ? "7px solid #e0e5ec" : "7px solid black";
  let t14;
  if ($[14] !== t13) {
    t14 = <span className="absolute sm:hidden left-1/2 -translate-x-1/2 top-[-5px] w-0 h-0" style={{
      borderLeft: "7px solid transparent",
      borderRight: "7px solid transparent",
      borderBottom: t13
    }} />;
    $[14] = t13;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  const t15 = isNeumorphic ? "10px solid transparent" : "10px solid white";
  let t16;
  if ($[16] !== t15) {
    t16 = <span className="hidden sm:block absolute left-1/2 -translate-x-1/2 bottom-[-10px] w-0 h-0" style={{
      borderLeft: "10px solid transparent",
      borderRight: "10px solid transparent",
      borderTop: t15
    }} />;
    $[16] = t15;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  const t17 = isNeumorphic ? "7px solid #e0e5ec" : "7px solid black";
  let t18;
  if ($[18] !== t17) {
    t18 = <span className="hidden sm:block absolute left-1/2 -translate-x-1/2 bottom-[-5px] w-0 h-0" style={{
      borderLeft: "7px solid transparent",
      borderRight: "7px solid transparent",
      borderTop: t17
    }} />;
    $[18] = t17;
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  let t19;
  if ($[20] !== t10 || $[21] !== t12 || $[22] !== t14 || $[23] !== t16 || $[24] !== t18 || $[25] !== t6) {
    t19 = <span aria-hidden="true" className={t6} style={t7}>{t10}{t12}{t14}{t16}{t18}</span>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t14;
    $[23] = t16;
    $[24] = t18;
    $[25] = t6;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== children || $[28] !== t19) {
    t20 = <span className="relative inline-block" onMouseEnter={show} onMouseLeave={hide} onTouchStart={t5} aria-label={"Egypt \u2013 Cairo location on map"}>{children}{t19}</span>;
    $[27] = children;
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  return t20;
}
function _temp(v) {
  return !v;
}